import type { MarketAccount, Security, UnifiedLiquidityToken } from '@talos/kyoko';
import {
  getUnifiedLiquiditySymbolCandidatesForMarket,
  UnifiedLiquidityMarketTooltipStatus,
  type UnifiedLiquidityMarketTooltipGroup,
} from './unifiedLiquidityMarketTooltip';
import { hasUnifiedLiquiditySelection } from './utils/unifiedLiquidity';

interface UnifiedLiquidityMarketSymbolSelection {
  marketAccount: string;
  symbol: string;
}

export function getSelectedUnifiedLiquidityMarketTooltipGroups({
  primarySymbol,
  primarySecurity,
  unifiedLiquidityTokens,
  selectedMarkets = [],
  selectedMarketAccounts,
  selectedUnifiedLiquidityMarketSymbols,
  marketAccountsByMarket = new Map(),
  marketAccountsByName,
  marketDisplayNameByName = new Map(),
  securitiesBySymbol,
  enablePrimarySymbolsWithoutSelection = false,
}: {
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
  selectedMarkets?: string[];
  selectedMarketAccounts: string[];
  selectedUnifiedLiquidityMarketSymbols: UnifiedLiquidityMarketSymbolSelection[];
  marketAccountsByMarket?: Map<string, MarketAccount[]>;
  marketAccountsByName: Map<string, MarketAccount>;
  marketDisplayNameByName?: Map<string, string>;
  securitiesBySymbol: Map<string, Security>;
  enablePrimarySymbolsWithoutSelection?: boolean;
}): UnifiedLiquidityMarketTooltipGroup[] {
  const selectedMarketAccountMarkets = new Set<string>();
  const marketAccountGroups = selectedMarketAccounts
    .map(marketAccountName => {
      const marketAccount = marketAccountsByName.get(marketAccountName);
      if (!marketAccount) {
        return undefined;
      }
      selectedMarketAccountMarkets.add(marketAccount.Market);

      return getSelectedMarketTooltipGroup({
        key: marketAccount.Name,
        displayName: marketAccount.DisplayName ?? marketAccount.Name,
        market: marketAccount.Market,
        marketAccountsForStatus: [marketAccount],
        primarySymbol,
        primarySecurity,
        unifiedLiquidityTokens,
        selectedUnifiedLiquidityMarketSymbols,
        securitiesBySymbol,
        enablePrimarySymbolsWithoutSelection,
      });
    })
    .filter((group): group is UnifiedLiquidityMarketTooltipGroup => group != null && group.rows.length > 0);

  const marketGroups = Array.from(new Set(selectedMarkets))
    .filter(market => !selectedMarketAccountMarkets.has(market))
    .map(market =>
      getSelectedMarketTooltipGroup({
        key: market,
        displayName: marketDisplayNameByName.get(market) ?? market,
        market,
        marketAccountsForStatus: marketAccountsByMarket.get(market) ?? [],
        primarySymbol,
        primarySecurity,
        unifiedLiquidityTokens,
        selectedUnifiedLiquidityMarketSymbols,
        securitiesBySymbol,
        enablePrimarySymbolsWithoutSelection,
      })
    )
    .filter((group): group is UnifiedLiquidityMarketTooltipGroup => group != null && group.rows.length > 0);

  return [...marketAccountGroups, ...marketGroups].toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

function getSelectedMarketTooltipGroup({
  key,
  displayName,
  market,
  marketAccountsForStatus,
  primarySymbol,
  primarySecurity,
  unifiedLiquidityTokens,
  selectedUnifiedLiquidityMarketSymbols,
  securitiesBySymbol,
  enablePrimarySymbolsWithoutSelection,
}: {
  key: string;
  displayName: string;
  market: string;
  marketAccountsForStatus: MarketAccount[];
  primarySymbol: string | undefined;
  primarySecurity: Security | undefined;
  unifiedLiquidityTokens: UnifiedLiquidityToken[];
  selectedUnifiedLiquidityMarketSymbols: UnifiedLiquidityMarketSymbolSelection[];
  securitiesBySymbol: Map<string, Security>;
  enablePrimarySymbolsWithoutSelection: boolean;
}): UnifiedLiquidityMarketTooltipGroup | undefined {
  const symbolCandidates = getUnifiedLiquiditySymbolCandidatesForMarket({
    market,
    primarySymbol,
    primarySecurity,
    unifiedLiquidityTokens,
    securitiesBySymbol,
  });
  if (!symbolCandidates.length) {
    return undefined;
  }

  return {
    key,
    displayName,
    market,
    rows: symbolCandidates
      .map(symbol => ({
        symbol,
        displayName: securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol,
        status: getSelectionStatus({
          marketAccounts: marketAccountsForStatus,
          symbol,
          primarySymbol,
          selectedUnifiedLiquidityMarketSymbols,
          enablePrimarySymbolsWithoutSelection,
        }),
      }))
      .toSorted((a, b) => a.displayName.localeCompare(b.displayName)),
  };
}

function getSelectionStatus({
  marketAccounts,
  symbol,
  primarySymbol,
  selectedUnifiedLiquidityMarketSymbols,
  enablePrimarySymbolsWithoutSelection,
}: {
  marketAccounts: MarketAccount[];
  symbol: string;
  primarySymbol: string | undefined;
  selectedUnifiedLiquidityMarketSymbols: UnifiedLiquidityMarketSymbolSelection[];
  enablePrimarySymbolsWithoutSelection: boolean;
}) {
  const isSelected = marketAccounts.some(marketAccount =>
    hasUnifiedLiquiditySelection(selectedUnifiedLiquidityMarketSymbols, {
      marketAccount: marketAccount.Name,
      symbol,
    })
  );

  return isSelected || (enablePrimarySymbolsWithoutSelection && symbol === primarySymbol)
    ? UnifiedLiquidityMarketTooltipStatus.Enabled
    : UnifiedLiquidityMarketTooltipStatus.Disabled;
}
